import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as s from './styled-events';
import { icons } from '../../assets';
import { newFormatDate, notifyError, notifySuccess } from '../../utils';
import { putRearmeEvento,} from '../../services';

// Mapping tipo -> cor + label para feedback visual de severidade.
// Alarme < Bloqueio < Trip em ordem crescente de gravidade. Cores via
// tokens CCO (trip mantem o vermelho escuro proprio — nao ha token).
const getEventoVisual = (tipo) => {
  const t = (tipo || '').toLowerCase();
  if (t === 'alarme') return { color: 'var(--cco-orange)', icon: icons.Evento };
  if (t === 'trip') return { color: '#7A1818', icon: icons.EventoRed };
  return { color: 'var(--cco-red)', icon: icons.EventoRed };
};

const Eventos = () => {
  const [open, setOpen] = useState(false);
  const [fadeout, setFadeout] = useState(false);
  const [tag, setTag] = useState('Todos');
  const { quantidade, eventos } = useSelector((state) => state.eventos);
  const history = useHistory();
  const dispatch = useDispatch();

  const buscaEventos = useCallback(() => {
    dispatch({
      type: 'EVENTOS/SAGA_BUSCA_QUANTIDADE',
    });
  }, [dispatch]);

  useEffect(() => {
    if (open || !eventos.length) buscaEventos();
    else
      dispatch({
        type: 'EVENTOS/SET_ALARME',
      });
  }, [open, eventos.length, buscaEventos, dispatch]);

  const handlePutAlarme = (id) => {
    putRearmeEvento({
      thingId: parseInt(id, 10),
    })
      .then(({ data }) => {
        // Feedback via toast (antes era um textarea readonly flutuante —
        // pouco visivel e fora do padrao). O backend manda multilinhas
        // separadas por <br/>; viram itens " - " num toast unico.
        notifySuccess(
          (data.message || 'Rearme enviado')
            .split('<br/>')
            .filter(Boolean)
            .join(' - '),
        );
        const newFilter = {
          total: 0,
        };
        data.filter.forEach(({ label, count }) => {
          newFilter[label] = count;
          newFilter.total += parseInt(count, 10);
        });
        dispatch({
          type: 'EVENTOS/SET_EVENTOS',
          eventos: data.list,
          quantidade: newFilter,
        });
      })
      .catch(() => {
        notifyError('Falha ao rearmar a thing');
      });
  };

  // const handleLeitura = (id) => {
  //   postReadNotification({ id })
  //     .then(() => {
  //       buscaEventos();
  //     })
  //     .catch(() => notifyError('Falha ao remover o evento'));
  // };

  if (!open)
    return (
      <s.ClosedContainer
        as="button"
        type="button"
        onClick={() => setOpen(true)}
        className={quantidade.total > 0 ? 'pulse' : ''}
        aria-label={
          quantidade.total > 0
            ? `Abrir painel de eventos (${quantidade.total} ativos)`
            : 'Abrir painel de eventos'
        }
        title={
          quantidade.total > 0
            ? `${quantidade.total} eventos ativos`
            : 'Painel de eventos'
        }
      >
        <div>
          <label>{quantidade.total || ''}</label>
          <img src={icons.Evento} alt="" />
        </div>
      </s.ClosedContainer>
    );

  return (
    <>
      <s.OpenContainer
        className={
          !fadeout ? 'appear-from-top-right' : 'dissapear-from-top-right'
        }
      >
        <s.Line flex={1}>
          <s.Line>
            <s.IconWrapper unclickable>
              <img src={icons.EventoRed} alt="" />
            </s.IconWrapper>
            <label>{quantidade.total} EVENTOS</label>
          </s.Line>

          <s.IconWrapper
            as="button"
            type="button"
            aria-label="Fechar painel de eventos"
            title="Fechar"
            onClick={() => {
              setFadeout(true);
              setTimeout(() => {
                setFadeout(false);
                setOpen(false);
              }, 270);
            }}
          >
            x
          </s.IconWrapper>
        </s.Line>

        <s.Line flex={1}>
          <s.Tag
            as="button"
            type="button"
            onClick={() => setTag('Todos')}
            aria-pressed={tag === 'Todos'}
          >
            TODOS
          </s.Tag>
          <s.Tag
            as="button"
            type="button"
            onClick={() => quantidade.Alarme && setTag('Alarme')}
            disabled={!quantidade.Alarme}
            aria-pressed={tag === 'Alarme'}
          >
            {quantidade.Alarme || ' '} ALARMES
          </s.Tag>
          <s.Tag
            as="button"
            type="button"
            onClick={() => quantidade.Bloqueio && setTag('Bloqueio')}
            disabled={!quantidade.Bloqueio}
            aria-pressed={tag === 'Bloqueio'}
          >
            {quantidade.Bloqueio || ''} BLOQUEIOS
          </s.Tag>
          <s.Tag
            as="button"
            type="button"
            onClick={() => quantidade.Trip && setTag('Trip')}
            disabled={!quantidade.Trip}
            aria-pressed={tag === 'Trip'}
          >
            {quantidade.Trip || ''} TRIPS
          </s.Tag>
        </s.Line>

        <s.Column flex={10}>
          {eventos.length ? (
            eventos
              .filter(({ type }) => type.label === tag || tag === 'Todos')
              .map((evento) => {
                const visual = getEventoVisual(evento.type && evento.type.label);
                return (
                  <s.Evento key={evento.id ?? `${evento.thing?.id}-${evento.timestamp}`}>
                    <s.IconWrapper color={visual.color} unclickable>
                      <img
                        src={visual.icon}
                        alt={`Tipo: ${evento.type && evento.type.label}`}
                      />
                    </s.IconWrapper>
                    <h3>{evento.thing.thing}</h3>
                    <s.TypeBadge tipo={evento.type && evento.type.label}>
                      {evento.type && evento.type.label}
                    </s.TypeBadge>
                    <h4>{newFormatDate(evento.timestamp)}</h4>
                    <label>
                      {evento.alarmOperationalHistoric.variable.name}
                    </label>
                    {evento.hasRearm ? (
                      <s.EventButton
                        type="button"
                        onClick={() =>
                          handlePutAlarme(parseInt(evento.thing.id, 10))
                        }
                      >
                        REARMAR
                      </s.EventButton>
                    ) : (
                      <div>{null}</div>
                    )}
                    <s.EventButton
                      type="button"
                      onClick={() => {
                        history.push('/dashboard/customer/operational', {
                          thing: evento.thing,
                          aplicacao: evento.appliance,
                        });
                        setFadeout(true);
                        setTimeout(() => {
                          setFadeout(false);
                          setOpen(false);
                        }, 270);
                      }}
                      isRed
                    >
                      VER AGORA
                    </s.EventButton>
                    <s.IconWrapper
                      // onClick={() => handleLeitura(evento.id)}
                      color="rgba(227, 233, 235, 1)"
                    >
                      <div>x</div>
                    </s.IconWrapper>
                  </s.Evento>
                );
              })
          ) : (
            <s.EmptyLabel>Não há eventos a serem exibidos</s.EmptyLabel>
          )}
        </s.Column>
      </s.OpenContainer>
    </>
  );
};

export default Eventos;
